import React, {useState} from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  toggleButtonGroupClasses,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import SearchIcon from '@mui/icons-material/Search';
import {Sx} from 'app/types/common';
import {DatesPickerDialog, Files} from 'app/components/entities/files';
import {TotalAndSize} from 'app/components/features/files';

type Interval = 'all' | 'today' | 'yesterday' | '7days' | '30days' | 'custom';

interface Props extends Sx {
  recordings: Files.Recording[];
  search: string;
  allSelected: boolean;
  hasSelectable: boolean;
  sharedOnly: boolean;
  period?: [number, number];
  onSelectAll: () => void;
  onSetPeriod: (date: [number, number] | undefined) => void;
  onSearch: (search: string) => void;
  onSharedOnly: (shared: boolean) => void;
}

export function FilesToolbar({
  sx,
  recordings,
  search,
  allSelected,
  hasSelectable,
  sharedOnly,
  period,
  onSelectAll,
  onSearch,
  onSetPeriod,
  onSharedOnly,
}: Props) {
  const [datesDialog, setDatesDialog] = useState(false);
  const [option, setOption] = useState<Interval>('all');

  const handleSelect = (period: [number, number]) => {
    setOption('custom');
    onSetPeriod(period);
    setDatesDialog(false);
  };

  const handleToggle = (_: unknown, value: any) => {
    onSharedOnly(value === 'shared');
  };

  return (
    <Box sx={sx}>
      <TotalAndSize sx={{mb: 1}} dataId="files-summary" recordings={recordings} />

      <Stack mb={1} direction="row" alignItems="center" gap={1}>
        <Button
          data-id="select-all-btn"
          sx={{minWidth: 106}}
          variant="outlined"
          color="info"
          disabled={!hasSelectable}
          startIcon={allSelected ? <CheckBoxIcon color="primary" /> : <CheckBoxOutlineBlankIcon />}
          onClick={onSelectAll}
        >
          {allSelected ? 'Deselect' : 'Select all'}
        </Button>

        <ToggleButtonGroup
          sx={{[`& .${toggleButtonGroupClasses.grouped}`]: {py: '5px'}}}
          exclusive={true}
          size="medium"
          value={sharedOnly ? 'shared' : 'all'}
          color="primary"
          onChange={handleToggle}
        >
          <ToggleButton data-id="all-files-button" value="all">
            All files
          </ToggleButton>
          <ToggleButton data-id="shared-files-button" value="shared">
            Shared files
          </ToggleButton>
        </ToggleButtonGroup>

        <TextField
          data-id="dates-select"
          sx={{ml: 'auto'}}
          select={true}
          value={option}
          InputProps={{sx: {pl: 1}, startAdornment: <InsertInvitationIcon color="primary" />}}
          SelectProps={{
            renderValue: (value: unknown) => {
              const interval = value as Interval;

              if (interval === 'all') {
                return 'All time';
              }

              if (period) {
                return formatPeriod(period);
              }

              return null;
            },
            onChange: (event) => {
              const value = event.target.value as Interval;

              if (value !== 'custom') {
                setOption(value);
                onSetPeriod(getPeriod(value));
              }
            },
          }}
        >
          <MenuItem data-id="all-option" value="all">
            All time
          </MenuItem>
          <MenuItem data-id="today-option" value="today">
            Today
          </MenuItem>
          <MenuItem data-id="yesterday-option" value="yesterday">
            Yesterday
          </MenuItem>
          <MenuItem data-id="week-option" value="7days">
            7 days
          </MenuItem>
          <MenuItem data-id="month-option" value="30days">
            30 days
          </MenuItem>
          <MenuItem data-id="custom-option" value="custom" onClick={() => setDatesDialog(true)}>
            Custom dates
          </MenuItem>
        </TextField>

        <TextField
          sx={{width: 240}}
          value={search}
          InputProps={{startAdornment: <SearchIcon />, placeholder: 'Search'}}
          inputProps={{'data-id': 'search-input'}}
          onChange={(event) => onSearch(event.target.value)}
        />
      </Stack>

      <DatesPickerDialog
        open={datesDialog}
        period={period}
        onSelect={handleSelect}
        onClose={() => setDatesDialog(false)}
      />
    </Box>
  );
}

function formatPeriod(period: [number, number]): string {
  const start = dayjs.unix(period[0]);
  const end = dayjs.unix(period[1]);

  if (start.isSame(end, 'day')) {
    return start.format('MMM D');
  }

  return `${start.format('MMM D')} - ${end.format('MMM D')}`;
}

function getPeriod(interval: Interval): [number, number] | undefined {
  const now = dayjs();
  const today = now.startOf('day');

  switch (interval) {
    case 'today':
      return [today.unix(), today.endOf('day').unix()];

    case 'yesterday': {
      const yesterday = today.subtract(1, 'day');
      return [yesterday.unix(), yesterday.endOf('day').unix()];
    }

    case '7days':
      return [today.subtract(7, 'day').unix(), today.endOf('day').unix()];

    case '30days':
      return [today.subtract(30, 'day').unix(), today.endOf('day').unix()];

    default:
      return undefined;
  }
}
